<template>
  <LoadIcon
    v-if="loading"
    style="width: 100%;"
  />
  <div
    v-else-if="timeline && selectedSurvey"
    class="body"
  >
    <div
      class="surveys"
      tabindex="-1"
      data-testid="surveyDropdown"
      @focusout="handleFocusOut"
    >
      <p>Survey Date:</p>
      <div class="surveys-right">
        <button
          data-testid="survey-btn"
          class="npbtn npbtn-outlined"
          :class="{expanded: active}"
          aria-haspopup="true"
          tabindex="0"
          @click="active = !active"
        >
          <span>{{ dateFormatter_US.format(new Date(selectedSurvey.captureDateTime)) }}</span>
          <CaretIcon class="caret" />
        </button>
        <div
          v-if="active"
          class="dropdown"
        >
          <div class="dropdown-container">
            <div
              v-for="(val, index) in timeline"
              :key="index"
              class="dropdown-option"
              :data-testid="'year-' + index"
            >
              <p>{{ val.year }}</p>
              <span
                v-for="(survey, i) in val.surveys"
                :key="i"
                :data-testid="`${val.year}-survey-${i}`"
                :class="selectedSurvey.id === survey.id ? 'selected-survey' : 'survey'"
                @click="selectedSurvey = survey; active = false;"
              >{{ new Date(survey.captureDateTime).toLocaleDateString('en-US', {month: 'long', day: 'numeric'}) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="estimatedUsage"
      class="usage"
    >
      <p>Your usage this month:</p>
      <span data-testid="estimatedUsage">${{ estimatedUsage.toFixed(2) }}</span>
    </div>
  </div>
  <div
    v-else
    style="text-align: center;"
  >
    No drone imagery available for current location
  </div>
</template>

<script>
import {MapService} from '@/services/MapService';
import {mapState} from 'vuex';
import {Graphics} from '@/services/GraphicService';
import CaretIcon from '@/components/svgs/CaretIcon.vue';
import LoadIcon from '@/components/svgs/LoadIcon.vue';
import {wrapError} from '@northpoint-development/labs-common-core';


export default {
  name: 'SurveyDropdown',
  components: {
    CaretIcon,
    LoadIcon,
  },
  props: {
    layer: {type: Object, required: true},
  },
  data() {
    return {
      loading: true,
      selectedSurvey: null,
      timeline: null,
      droneGraphic: null,
      active: false,
    };
  },
  computed: {
    ...mapState(['dateFormatter_US']),
    /**
     * The average size of a tile image being returned from the Tile API is 25kB, and on
     * our current plan we are paying 30 cents per MB
     * @return {int} The calculated price estimate for the current user this month
     */
    estimatedUsage() {
      const dataRate = this.layer.rate ?? 0.3; // dollars per MB
      const showUsageThreshold = (this.layer.showUsageThreshold ?? 20); // dollar threshold
      const tileMB = 0.025; // MB per tile (25kB)
      const usage = this.droneGraphic.currentMonthUsage * tileMB * dataRate; // currentMonthUsage is in # of tiles
      return usage > showUsageThreshold ? usage : 0;
    },
  },
  watch: {
    selectedSurvey: {
      deep: true,
      async handler(newVal) {
        if (!newVal) return;
        // Initialize the new layer using the survey id
        this.droneGraphic.createLayer(newVal.id);
      },
    },
  },
  async mounted() {
    try {
      // Fetch surveys from the drone graphic's `getSurveys` method
      this.droneGraphic = Graphics.getGraphic('drone', 'drone');
      await this.handleSurveys();

      // Map idle function for polling for new surveys on idle
      let timer;
      MapService.addMapListener('nearmapsCoverage', 'idle', () => {
        clearTimeout(timer);
        timer = setTimeout(async () => {
          this.handleSurveys();
        }, 1500);
      });
    } catch (err) {
      console.error(wrapError(err, 'Error fetching drone surveys'));
    }
  },
  unmounted() {
    MapService.removeMapListener('nearmapsCoverage');
  },
  methods: {
    handleFocusOut() {
      // Don't close options if a child of '.dropdown' is focused or selected
      if (this.$el.matches(':focus-within')) return;
      this.active = false;
    },
    /**
     * Fetches surveys from the Drone Graphic's `getSurveys` method and sorts them into a
     * structure that can be used with the custom timeline markup
     */
    async handleSurveys() {
      this.loading = true;
      try {
        const polledSurveys = await this.droneGraphic.getSurveys();
        // On intial load, just set the selected survey to the most recent survey
        if (!this.selectedSurvey) {
          this.selectedSurvey = polledSurveys[0];
        }
        let needsUpdate = true;
        const output = [];
        polledSurveys.forEach((survey) => {
          // If the current survey exists in all returned survey then we shouldn't update its value,
          // Thus preventing the re-creation of a new image overlay if it isn't needed
          if (survey.id === this.selectedSurvey?.id) {
            needsUpdate = false;
          }
          const year = new Date(survey.captureDate).getFullYear();
          const existing = output.find((a) => a.year === year);
          if (existing) {
            existing.surveys.push(survey);
          } else {
            output.push({year: year, surveys: [survey]});
          }
        });
        this.timeline = output;
        if (needsUpdate) {
          this.selectedSurvey = polledSurveys[0];
        }
      } catch (err) {
        console.log(err);
      }
      this.loading = false;
    },
  },
};
</script>

<style scoped lang="scss">
p {
    color: main.$npGray;
    font-size: 14px;
    font-weight: bold;
  }
  .usage {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    margin-top: 20px;

    span {
      color: main.$npOrange;
      font-weight: 900;
    }
  }

.body {
  padding-right: 15px;
}
.surveys {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  &-right {
    width: 50%;
    margin-right: 10px;
    height: 40px;
  }

  p {
    width: 50%;
  }


    .dropdown {
      position: relative;
      z-index: 999;
      display: block;
      flex-direction: column;

      &-container {
        position: absolute;
        top: -8px;
        display: flex;
        flex-direction: column;
        height: 200px;
        width: 150px;
        overflow-y: overlay;
        box-shadow: 0px 1px 6px #00000033;
        background-color: white;
      }

      &-option {
        display: flex;
        flex-direction: column;

        p {
          padding: 5px 10px;
          border-bottom: solid 2px main.$npOrange;
          background-color: white;
          width: 35%;
          z-index: 999;
        }
        span {
          padding: 7px;
          display: flex;
          align-items: center;
        }
        .survey {
          background-color: white;
          transition: background-color .2s;
          cursor: pointer;
           &:hover {
            background-color: main.$lightGray;
          }
          &:before {
            content: '';
            width: 10px;
            height: 10px;
            background-color: white;
            border: 2px solid main.$npOrange;
            border-radius: 50%;
            margin-right: 10px;
            z-index: 998;
          }

          &:not(:last-child) {
            &:after {
              content: ' ';
              position: absolute;
              left: 11px;
              z-index: 997;
              border-left: solid main.$npOrange 2px;
              height: 110%;
              width: 1px;
            }
          }

        }

         .selected-survey {
          &:before {
            content: '';
            width: 10px;
            height: 10px;
            background-color: main.$npOrange;
            border: 2px solid main.$npOrange;
            border-radius: 50%;
            margin-right: 10px;
          }
        }

      }
    }

  }

 .npbtn {
      color: main.$npGray;
      font-size: 14px;
      width: 150px;
      font-weight: bold;
      &.expanded {
        .caret {
          transform: rotate(180deg) scale(1.5);
        }
      }
    }


  .caret {
    float: right;
    stroke: main.$npGray;
    margin-top: -2px;
    margin-right: -2px;
    height: 20px;
    width: 25px;
  }

</style>
